import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { goalsAPI } from '../services/api';
import type { GoalCreate, GoalUpdate, GoalStatus } from '../types';
import { useErrorHandler } from './useErrorHandler';

export const useGoals = (statusFilter?: GoalStatus) => {
  const queryClient = useQueryClient();
  const { handleError } = useErrorHandler();

  const { data: goals = [], isLoading, error } = useQuery({
    queryKey: ['goals', { status: statusFilter }],
    queryFn: () => goalsAPI.getAll(statusFilter ? { status: statusFilter } : undefined),
    retry: 1,
    onError: handleError,
  });

  const createMutation = useMutation({
    mutationFn: (goal: GoalCreate) => goalsAPI.create(goal),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goals'] });
    },
    onError: handleError,
  });

  const updateMutation = useMutation({
    mutationFn: ({ goalId, update }: { goalId: string; update: GoalUpdate }) =>
      goalsAPI.update(goalId, update),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goals'] });
    },
    onError: handleError,
  });

  const deleteMutation = useMutation({
    mutationFn: (goalId: string) => goalsAPI.delete(goalId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goals'] });
    },
    onError: handleError,
  });

  return {
    goals,
    isLoading,
    error: error as Error | null,
    createGoal: createMutation.mutateAsync,
    updateGoal: updateMutation.mutateAsync,
    deleteGoal: deleteMutation.mutateAsync,
  };
};
